import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
export default class VariantPicker extends Component {
#pendingRequestUrl;
#abortController;
#checkedIndices = [];
#radios = [];
#resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());
connectedCallback() {
super.connectedCallback();
if (this.dataset.templateProductMatch === 'true') {
const url = new URL(window.location.href);
if (url.searchParams.has('variant')) {
url.searchParams.delete('variant');
history.replaceState({}, '', url.toString());
      }
    }
const fieldsets = this.refs.fieldsets || [];
fieldsets.forEach((fieldset) => {
const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
this.#radios.push(radios);
const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
if (initialCheckedIndex !== -1) {
this.#checkedIndices.push([initialCheckedIndex]);
      }
    });
this.addEventListener('change', this.variantChanged.bind(this));
this.#resizeObserver.observe(this);
  }
disconnectedCallback() {
super.disconnectedCallback();
this.#resizeObserver.disconnect();
  }
variantChanged(event) {
if (!(event.target instanceof HTMLElement)) return;
const selectedOption =
event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
if (!selectedOption) return;
this.updateSelectedOption(event.target);
this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));
const isOnProductPage =
this.dataset.templateProductMatch === 'true' &&
!event.target.closest('product-card') &&
!event.target.closest('quick-add-dialog');
const currentUrl = this.dataset.productUrl?.split('?')[0];
const newUrl = selectedOption.dataset.connectedProductUrl;
const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));
const morphElementSelector = loadsNewProduct
? 'main'
: isOnFeaturedProductSection
? 'featured-product-information'
: undefined;
this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);
const url = new URL(window.location.href);
if (isOnProductPage) {
url.searchParams.delete('variant');
    }
if (loadsNewProduct) {
url.pathname = newUrl;
    }
if (url.href !== window.location.href) {
yieldToMainThread().then(() => {
history.replaceState({}, '', url.toString());
      });
    }
  }
  #getFieldsetMeasurements(fieldsetIndex) {
const fieldsets = this.refs.fieldsets || [];
const fieldset = fieldsets[fieldsetIndex];
const checkedIndices = this.#checkedIndices[fieldsetIndex];
const radios = this.#radios[fieldsetIndex];
if (!radios || !checkedIndices || !fieldset) return null;
const [currentIndex, previousIndex] = checkedIndices;
return {
fieldset,
currentIndex,
previousIndex,
currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
if (currentWidth) {
fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
fieldset.style.removeProperty('--pill-width-current');
    }
if (previousWidth) {
fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
fieldset.style.removeProperty('--pill-width-previous');
    }
  }
updateFieldsetCss(fieldsetIndex) {
if (Number.isNaN(fieldsetIndex)) return;
const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
if (measurements) {
this.#applyFieldsetMeasurements(measurements);
    }
  }
updateSelectedOption(target) {
if (typeof target === 'string') {
const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
if (!targetElement) throw new Error('Target element not found');
target = targetElement;
    }
if (target instanceof HTMLInputElement) {
const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
const inputIndex = Number.parseInt(target.dataset.inputIndex || '');
if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
const fieldsets = this.refs.fieldsets || [];
const fieldset = fieldsets[fieldsetIndex];
const checkedIndices = this.#checkedIndices[fieldsetIndex];
const radios = this.#radios[fieldsetIndex];
if (radios && checkedIndices && fieldset) {
const [currentIndex, previousIndex] = checkedIndices;
if (currentIndex !== undefined && radios[currentIndex]) {
radios[currentIndex].dataset.previousChecked = 'false';
          }
if (previousIndex !== undefined && radios[previousIndex]) {
radios[previousIndex].dataset.previousChecked = 'false';
          }
checkedIndices.unshift(inputIndex);
checkedIndices.length = Math.min(checkedIndices.length, 2);
const newCurrentIndex = checkedIndices[0];
const newPreviousIndex = checkedIndices[1];
if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
radios[newCurrentIndex].dataset.currentChecked = 'true';
          }
if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
radios[newPreviousIndex].dataset.previousChecked = 'true';
radios[newPreviousIndex].dataset.currentChecked = 'false';
          }
this.updateFieldsetCss(fieldsetIndex);
        }
      }
target.checked = true;
    }
if (target instanceof HTMLSelectElement) {
const newValue = target.value;
const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
if (!newSelectedOption) throw new Error('Option not found');
for (const option of target.options) {
option.removeAttribute('selected');
      }
newSelectedOption.setAttribute('selected', 'selected');
    }
  }
buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
this.#pendingRequestUrl = productUrl;
const params = [];
const viewParamValue = getViewParameterValue();
if (viewParamValue) params.push(`view=${viewParamValue}`);
if (this.selectedOptionsValues.length && !source) {
params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
if (this.selectedOptionsValues.length) {
params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }
const SECTION_ID_MAP = {
'quick-add-component': 'section-rendering-product-card',
'swatches-variant-picker-component': 'section-rendering-product-card',
'featured-product-information': this.closest('featured-product-information')?.id,
    };
const closestSectionId = Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId));
if (closestSectionId) {
if (productUrl?.includes('?')) {
productUrl = productUrl.split('?')[0];
      }
return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }
return `${productUrl}?${params.join('&')}`;
  }
fetchUpdatedSection(requestUrl, morphElementSelector) {
this.#abortController?.abort();
this.#abortController = new AbortController();
fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
this.#pendingRequestUrl = undefined;
const html = new DOMParser().parseFromString(responseText, 'text/html');
html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
if (!textContent) return;
if (morphElementSelector === 'main') {
this.updateMain(html);
        } else if (morphElementSelector) {
this.updateElement(html, morphElementSelector);
        } else {
const newProduct = this.updateVariantPicker(html);
if (this.selectedOptionId) {
this.dispatchEvent(
new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
html,
productId: this.dataset.productId ?? '',
newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
if (error.name === 'AbortError') {
console.warn('Fetch aborted by user');
        } else {
console.error(error);
        }
      });
  }
updateVariantPicker(newHtml) {
let newProduct;
const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
if (!newVariantPickerSource) {
throw new Error('No new variant picker source found');
    }
if (newVariantPickerSource instanceof HTMLElement) {
const newProductId = newVariantPickerSource.dataset.productId;
const newProductUrl = newVariantPickerSource.dataset.productUrl;
if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
newProduct = { id: newProductId, url: newProductUrl };
      }
this.dataset.productId = newProductId;
this.dataset.productUrl = newProductUrl;
    }
morph(this, newVariantPickerSource, {
...MORPH_OPTIONS,
getNodeKey: (node) => {
if (!(node instanceof HTMLElement)) return undefined;
return node.dataset.key;
      },
    });
this.updateVariantPickerCss();
return newProduct;
  }
updateVariantPickerCss() {
const fieldsets = this.refs.fieldsets || [];
const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);
for (const measurement of measurements) {
this.#applyFieldsetMeasurements(measurement);
    }
  }
updateElement(newHtml, elementSelector) {
const element = this.closest(elementSelector);
const newElement = newHtml.querySelector(elementSelector);
if (!element || !newElement) {
throw new Error(`No new element source found for ${elementSelector}`);
    }
morph(element, newElement);
  }
updateMain(newHtml) {
const main = document.querySelector('main');
const newMain = newHtml.querySelector('main');
if (!main || !newMain) {
throw new Error('No new main source found');
    }
morph(main, newMain);
  }
get selectedOption() {
const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
return undefined;
    }
return selectedOption;
  }
get selectedOptionId() {
const { selectedOption } = this;
if (!selectedOption) return undefined;
const { optionValueId } = selectedOption.dataset;
if (!optionValueId) {
throw new Error('No option value ID found');
    }
return optionValueId;
  }
get selectedOptionsValues() {
const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
return selectedOptions.map((option) => {
const { optionValueId } = option.dataset;
if (!optionValueId) throw new Error('No option value ID found');
return optionValueId;
    });
  }
}
if (!customElements.get('variant-picker')) {
customElements.define('variant-picker', VariantPicker);
}